import { ImageUtils } from './../../../helper/image-utils';
import { PartnerModel } from './../../../../module/partner/model/partner.model';
import { PartnerService } from './../../../../module/partner/partner.service';
import {
    Component,
    Input,
    ViewChild,
    TemplateRef,
    ElementRef,
    ViewContainerRef,
    Output,
    EventEmitter, OnInit, OnChanges, SimpleChanges
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { PaginatedModel } from '../../select/pagination/model/paginated.model';

@Component({
    selector: 'app-partner-array-input',
    templateUrl: './partner-array-input.component.html',
    styleUrl: './partner-array-input.component.scss'
})
export class PartnerArrayInputComponent implements OnInit, OnChanges {
    private _value: Array<string> = [];

    @Input()
    set value(value: Array<string>) {
        this._value = value ?? [];
        if (this.initialized) {
            this.initializePartners();
        }
    }
    get value(): Array<string> {
        return this._value;
    }
    @Input() placeholder = 'Selecione os parceiros';
    @Input() label = 'parceiro';

    @Output() changed = new EventEmitter<Array<PartnerModel>>();

    @ViewChild('trigger') trigger!: ElementRef;
    @ViewChild('dropdownTemplate') menuTemplate!: TemplateRef<any>;

    overlayRef!: OverlayRef;

    partnerFilterValue: string = '';
    partners: Array<PartnerModel> = [];
    selectedPartners = new Set<PartnerModel>();
    requestedPartners = new Set<string>();
    request: Subscription = new Subscription();
    searching = true;
    initialized = false;

    constructor(
        protected partnerService: PartnerService,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef
    ) {}

    public ngOnInit(): void {
        this.initialized = true;
        this.searchPartners();
        this.initializePartners();
    }
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) {
            this.initializePartners();
        }
    }
    protected initializePartners(): void {
        if (!this.value.length) {
            return;
        }
        (this.value as Array<string>).forEach((partnerId) => {
            if (this.requestedPartners.has(partnerId)) {
                return;
            }
            this.requestedPartners.add(partnerId);
            this.partnerService.getPartner(partnerId).subscribe((partner: PartnerModel) => {
                if (!this.hasPartner(partner)) {
                    this.selectedPartners.add(partner);
                    this.changed.emit([...this.selectedPartners]);
                }
            });
        });
    }
    public eventOpenOptions(): void {
        if (this.searching) {
            return;
        }
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.trigger)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 8,
                }
            ]);

        const triggerWidth = this.trigger.nativeElement.getBoundingClientRect().width;
        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            width: triggerWidth,
            minWidth: triggerWidth,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        this.overlayRef.backdropClick().subscribe(() => { this.overlayRef.dispose();});

        const menuTemplateBuilt = new TemplatePortal(this.menuTemplate, this.viewContainerRef);

        this.overlayRef.attach(menuTemplateBuilt);
    }

    protected handlePartners(partners: PaginatedModel<PartnerModel>): void {
        this.partners = partners.items.map(elem => ({
            ...elem,
            logoSrc: this.renderLogo(elem.logo)
        }));
    }
    public eventPartnerChanged(partner: PartnerModel): void {
        if (this.hasPartner(partner)) {
            this.selectedPartners.delete(partner);
        } else {
            this.selectedPartners.add(partner);
        }
        this.changed.emit([...this.selectedPartners]);

        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }
    public eventRemovePartner(partner: PartnerModel): void {
        this.selectedPartners.delete(partner);
        this.changed.emit([...this.selectedPartners]);
    }

    public eventPartnerFilterChange(value: Event): void {
        this.partnerFilterValue = (value.target as HTMLInputElement).value;
        this.searchPartnersByFilter(this.partnerFilterValue);
    }

    protected searchPartners(): void {
        this.request.unsubscribe();
        this.searchPartnersByFilter('', true);
    }

    protected searchPartnersByFilter(filter: string, immediate = false): void {
        this.request.unsubscribe();
        this.searching = true;

        const request$ = immediate
            ? this.partnerService.getPartners(filter)
            : this.partnerService.getPartners(filter).pipe(debounceTime(600), distinctUntilChanged());

        this.request = request$.subscribe((partners) => {
            this.searching = false;
            this.handlePartners(partners);
        });
    }

    private renderLogo(logo: string) {
        return ImageUtils.convertValueToImg(logo);
    }
    public hasPartner(partner: PartnerModel): boolean {
        return [...this.selectedPartners].some((selected) => selected._id === partner._id);
    }
}
